import { useState, useEffect, useRef, type UIEvent } from 'react';
import Button from '../../components/Button';
import Tab from '../../components/Tab';
import MedicationChangeRequestCard from './MedicationChangeRequestCard';

interface MedicationChangeRequest {
    id: number;
    patient_name?: string | null;
    prescription_number?: string | null;
    [key: string]: unknown;
}

interface Pagination {
    current_page?: number | null;
    total_pages?: number | null;
}

interface ManageMedicationChangeRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    pendingRequestChanges: MedicationChangeRequest[];
    completedRequestChanges: MedicationChangeRequest[];
    pendingPagination?: Pagination | null;
    completedPagination?: Pagination | null;
    isPendingLoading?: boolean;
    isCompletedLoading?: boolean;
    onLoadMorePending?: () => void;
    onLoadMoreCompleted?: () => void;
    onProcessRequest: (requestChange: MedicationChangeRequest) => void;
}

const TAB_OPTIONS = [
    'รอดำเนินการ',
    'เสร็จสิ้น',
] as const;

type TabOption = typeof TAB_OPTIONS[number];

/**
* 
* ManageMedicationChangeRequestModal
* 
* A modal component for managing medication change requests.
*
* @param {object} props
* @param {boolean} props.isOpen
*   Indicate whether the modal is open.
* @param {function} props.onClose
*  Function to call when closing the modal.
* @param {array} props.requestChanges
*  List of medication change requests.
* @param {function} props.onProcessRequest
*  Function to call when processing a request.
*/
export default function ManageMedicationChangeRequestModal({
    isOpen,
    onClose,
    pendingRequestChanges,
    completedRequestChanges,
    pendingPagination,
    completedPagination,
    isPendingLoading = false,
    isCompletedLoading = false,
    onLoadMorePending,
    onLoadMoreCompleted,
    onProcessRequest,
}: ManageMedicationChangeRequestModalProps) {

    const [activeTab, setActiveTab] = useState<TabOption>(TAB_OPTIONS[0]);
    const pendingScrollRef = useRef<HTMLDivElement | null>(null);
    const completedScrollRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setActiveTab(TAB_OPTIONS[0]);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const today = new Date();
    const formattedDate = today.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    function buildFooter() {
        return (
            <div className="flex justify-end items-center gap-3">
                <Button hierarchy="secondary" size="md" text="ปิด" className="w-[120px]" onClick={onClose} />
            </div>
        );
    }

    function buildEmptyRequest() {
        return (
            <div className="flex rounded-md p-5 items-center justify-center bg-main-background-system-default-dim" style={{ height: "360px" }}>
                <span className="text-body-base text-main-typography-system-caption">ยังไม่มีคำร้องแก้ไขยา</span>
            </div>
        );
    }

    function buildPendingRequests() {
        if (pendingRequestChanges.length === 0) {
            return buildEmptyRequest();
        }

        return (
            <div
                ref={pendingScrollRef}
                data-testid="pending-request-scroll"
                className="flex flex-col rounded-lg h-[360px] p-5 gap-3 bg-main-background-system-default-dim overflow-y-auto custom-scrollbar"
                onScroll={(event: UIEvent<HTMLDivElement>) => {
                    if (!onLoadMorePending) return;
                    const target = event.currentTarget;
                    const threshold = 50;
                    const shouldLoadMore = target.scrollHeight - target.scrollTop - target.clientHeight <= threshold;
                    if (!shouldLoadMore) return;

                    const currentPage = Number(pendingPagination?.current_page ?? 1);
                    const totalPages = Number(pendingPagination?.total_pages ?? 1);
                    if (currentPage >= totalPages) return;
                    onLoadMorePending();
                }}
            >
                {pendingRequestChanges.map((requestChange) => (
                    <MedicationChangeRequestCard
                        key={requestChange.id}
                        requestChange={requestChange}
                        onProcessRequest={() => onProcessRequest(requestChange)}
                        status="pending"
                    />
                ))}
                {isPendingLoading && (
                    <span className="text-body-base text-main-typography-system-caption">กำลังโหลดเพิ่มเติม...</span>
                )}
            </div>
        );
    }

    function buildCompletedRequests() {
        if (completedRequestChanges.length === 0) {
            return buildEmptyRequest();
        }

        return (
            <div
                ref={completedScrollRef}
                data-testid="completed-request-scroll"
                className="flex flex-col rounded-lg h-[360px] p-5 gap-3 bg-main-background-system-default-dim overflow-y-auto custom-scrollbar"
                onScroll={(event: UIEvent<HTMLDivElement>) => {
                    if (!onLoadMoreCompleted) return;
                    const target = event.currentTarget;
                    const threshold = 50;
                    const shouldLoadMore = target.scrollHeight - target.scrollTop - target.clientHeight <= threshold;
                    if (!shouldLoadMore) return;

                    const currentPage = Number(completedPagination?.current_page ?? 1);
                    const totalPages = Number(completedPagination?.total_pages ?? 1);
                    if (currentPage >= totalPages) return;
                    onLoadMoreCompleted();
                }}
            >
                {completedRequestChanges.map((requestChange) => (
                    <MedicationChangeRequestCard
                        key={requestChange.id}
                        requestChange={requestChange}
                        status="completed"
                        onProcessRequest={() => {}}
                    />))}
                {isCompletedLoading && (
                    <span className="text-body-base text-main-typography-system-caption">กำลังโหลดเพิ่มเติม...</span>
                )}
            </div>
        );
    }

    return (
        <div className="modal-overlay custom-scrollbar">
            <div className="modal-content w-[954px]">
                <div className="flex flex-col gap-3">
                    <h2 className="font-semibold text-title text-main-typography-system-default">คำร้องแก้ไขยา</h2>
                    <span className="font-body text-body-base text-main-typography-system-description">จัดการคำร้องจากพนักงานหน้าร้านวันที่ {formattedDate}</span>
                </div>
                <div className="flex flex-col gap-5">
                    <div className="w-full border-b border-main-border-system-default">
                        <Tab
                            menus={TAB_OPTIONS}
                            defaultSelectedTabMenu={activeTab}
                            onClickTabMenu={setActiveTab}
                        />
                    </div>
                    {activeTab === TAB_OPTIONS[0] && buildPendingRequests()}
                    {activeTab === TAB_OPTIONS[1] && buildCompletedRequests()}
                </div>
                <hr className="border-t border-main-border-system-default w-full" />
                {buildFooter()}
            </div>
        </div>
    );
}
